import React, { useState, useEffect, useCallback } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableExerciseItem } from './SortableExerciseItem';
import ExerciseInfoDialog from './ExerciseInfoDialog';
import { 
  X, Save, AlertCircle, Loader2, Dumbbell, 
  Plus, Info, ChevronDown, ChevronUp, GripVertical, 
  Search, Filter, Timer, Trash2, ImageIcon, XCircle, Image, Edit, Eye
} from 'lucide-react';
import { debugLog } from '../../../utils/logger';

// Exercise tabs by muscle group
const MUSCLE_GROUPS = {
  'petto': 'Petto',
  'schiena': 'Schiena',
  'gambe': 'Gambe',
  'spalle': 'Spalle',
  'bicipiti': 'Bicipiti',
  'avambracci': 'Avambracci',
  'tricipiti': 'Tricipiti',
  'addominali': 'Addominali',
  'core': 'Core',
  'tutti': 'Tutti'
};

// Exercise types
const SET_TYPES = {
  'normal': 'Normale',
  'superset': 'Superset',
  'dropset': 'Drop Set',
  'piramidale': 'Piramidale',
  'circuit': 'Circuito',
  'giant_set': 'Giant Set'
};

// Lista validata dei tipi di set consentiti
const VALID_SET_TYPES = ['normal', 'superset', 'dropset', 'circuit', 'piramidale', 'giant_set'];

// Helper function per validare set_type
const validateSetType = (value) => {
  // Conversioni esplicite per 0 e 1
  if (value === 0 || value === '0') {
    return 'normal';
  }
  if (value === 1 || value === '1') {
    return 'superset';
  }
  
  // Gestisci valori nulli o vuoti
  if (value === null || value === '') {
    return 'normal';
  }
  
  // Converti a stringa e pulisci
  const cleanValue = String(value).trim().toLowerCase();
  
  // Tipi di set consentiti
  const validSetTypes = ['normal', 'superset', 'dropset', 'circuit', 'piramidale', 'giant_set'];
  
  // Verifica se è un tipo valido
  const result = validSetTypes.includes(cleanValue) ? cleanValue : 'normal';
  
  return result;
};

const ImprovedWorkoutCreator = ({
  user,
  onClose,
  onSuccess,
  mode = 'new', // 'new', 'edit', 'view'
  initialWorkout = null,
  availableExercises = [],
  api,
  onCustomSave = null,
  preloadedWorkoutData = null,
  editorConfig = null,
}) => {
  const labels = {
    titleNew: 'Nuova scheda allenamento',
    titleEdit: 'Modifica scheda allenamento',
    nomeLabel: 'Nome Scheda*',
    nomePlaceholder: 'es: Scheda A - Petto e Braccia',
    descrizionePlaceholder: 'Descrizione della scheda...',
    activeOn: 'Scheda attiva',
    activeOff: 'Scheda inattiva',
    saveNew: 'Salva scheda',
    saveEdit: 'Aggiorna',
    loading: 'Caricamento scheda...',
    nomeRequired: 'Il nome della scheda è obbligatorio',
    exercisesRequired: 'Aggiungi almeno un esercizio alla scheda',
    ...editorConfig,
  };
  // State for the workout data
  const [workoutData, setWorkoutData] = useState({
    nome: mode === 'new' ? `Scheda per ${user?.username || ''}` : '',
    descrizione: '',
    active: true, // Predefinito a true per nuove schede
    esercizi: []
  });

  const handleActiveToggle = () => {
    setWorkoutData(prev => ({
      ...prev,
      active: !prev.active
    }));
  };
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('petto');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedExercise, setExpandedExercise] = useState(null);
  const [selectedExerciseInfo, setSelectedExerciseInfo] = useState(null);
  const [workoutLoaded, setWorkoutLoaded] = useState(false);
  
  
  // DND sensors setup
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        if (workoutLoaded) return; // Previene caricamenti multipli
        
        setLoading(true);

        if (preloadedWorkoutData) {
          setWorkoutData(preloadedWorkoutData);
          setWorkoutLoaded(true);
          setLoading(false);
          return;
        }
        
        // Determina l'ID della scheda da caricare
        let schedaId = null;
        
        if (mode === 'edit' && initialWorkout) {
          // Se l'oggetto contiene direttamente l'ID della scheda
          if (initialWorkout.id && !initialWorkout.scheda_id) {
            schedaId = initialWorkout.id;
          } 
          // Se l'oggetto è un'assegnazione utente con scheda_id
          else if (initialWorkout.scheda_id) {
            schedaId = initialWorkout.scheda_id;
          }
          
          if (schedaId) {
            // Usa scheda_id per caricare i dettagli della scheda
            const response = await api.get(`/schede.php?id=${schedaId}`);
            const workoutDetails = response.data;
            
            if (workoutDetails) {
              setWorkoutData({
                nome: workoutDetails.nome || '',
                descrizione: workoutDetails.descrizione || '',
                active: workoutDetails.active === undefined ? true : !!parseInt(workoutDetails.active),
                esercizi: (workoutDetails.esercizi || []).map((ex, index) => {
                  // Trova l'esercizio completo per verificare se è cardio
                  const fullExercise = availableExercises.find(e => parseInt(e.id) === parseInt(ex.esercizio_id));
                  const isCardio = fullExercise?.is_cardio === 1;
                  
                  // Per esercizi cardio: converti secondi in minuti per l'UI
                  let ripetizioni = parseInt(ex.ripetizioni, 10) || 10;
                  if (isCardio && ripetizioni > 0) {
                    ripetizioni = Math.round(ripetizioni / 60);
                  }
                  
                  // 🔥 DB-001: Parse note JSON se presenti
                  let notes = { trainer: null, user: null, system: null };
                  if (ex.note && typeof ex.note === 'string' && ex.note.trim().startsWith('{')) {
                    try {
                      notes = JSON.parse(ex.note);
                    } catch (e) {
                      // Se non è JSON valido, considera come note trainer legacy
                      notes = { trainer: ex.note, user: null, system: null };
                    }
                  } else if (ex.note) {
                    // Note legacy (stringa semplice) → considera come note trainer
                    notes = { trainer: ex.note, user: null, system: null };
                  }
                  
                  // Se l'API restituisce già notes parsate, usale
                  if (ex.notes && typeof ex.notes === 'object') {
                    notes = {
                      trainer: ex.notes.trainer || null,
                      user: ex.notes.user || null,
                      system: ex.notes.system || null
                    };
                  }
                  
                  return {
                  ...ex,
                  tempId: `ex-${Date.now()}-${index}`,
                  esercizio_id: parseInt(ex.esercizio_id, 10),
                    serie: parseInt(ex.serie, 10) || (isCardio ? 1 : 3),
                    ripetizioni: ripetizioni,
                  peso: parseFloat(ex.peso) || 0,
                  tempo_recupero: parseInt(ex.tempo_recupero, 10) || 90,
                  note: ex.note || '',
                  notes: notes, // 🔥 DB-001: Aggiungi notes parsate
                  set_type: validateSetType(ex.set_type),
                    linked_to_previous: Boolean(ex.linked_to_previous),
                    is_isometric_scheda: ex.is_isometric_scheda || ex.is_isometric || 0,
                    calorie: ex.calorie || null,
                    bpm: ex.bpm || null
                  };
                })
              });
              
              // Imposta il flag per evitare caricamenti ripetuti
              setWorkoutLoaded(true);
            }
          }
        } else if (mode === 'new' && user) {
          // In modalità nuova scheda, imposta un titolo predefinito
          setWorkoutData({
            nome: `Scheda per ${user.username || ''}`,
            descrizione: '',
            esercizi: []
          });
          
          // Imposta il flag anche per le nuove schede
          setWorkoutLoaded(true);
        }
      } catch (err) {
        console.error('Error loading workout data:', err);
        setError('Errore nel caricamento dei dati della scheda');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [api, initialWorkout, mode, user, workoutLoaded, preloadedWorkoutData]);

  // Get exercise name helper
  const getExerciseName = useCallback((exerciseId) => {
    if (!exerciseId) return 'Senza nome';
    
    const exercise = availableExercises.find(e => parseInt(e.id) === parseInt(exerciseId));
    return exercise ? exercise.nome : 'Senza nome';
  }, [availableExercises]);
  
  // Add a new exercise to the workout
  const handleAddExercise = useCallback((exercise) => {
    const isCardio = exercise.is_cardio === 1;
    setWorkoutData(prev => ({
      ...prev,
      esercizi: [
        ...prev.esercizi,
        {
          tempId: `new-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          esercizio_id: parseInt(exercise.id, 10),
          serie: isCardio ? 1 : 3, // Cardio: sempre 1 serie
          ripetizioni: 12,
          peso: 0,
          tempo_recupero: 90,
          note: '',
          set_type: 'normal',
          linked_to_previous: false,
          ordine: prev.esercizi.length,
          is_isometric_scheda: 0, // Default: non isometrico a livello scheda
          calorie: null, // Default: nessuna caloria
          bpm: null // Default: nessun BPM
        }
      ]
    }));
  }, []);
  
  // Remove an exercise from the workout
  const handleRemoveExercise = useCallback((index) => {
    setWorkoutData(prev => ({
      ...prev,
      esercizi: prev.esercizi.filter((_, i) => i !== index)
    }));
  }, []);
  
  // Handle input field changes
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setWorkoutData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);
  
  // Handle exercise field changes
  const handleExerciseChange = useCallback((index, field, value) => {
    setWorkoutData(prev => {
      // Create a copy of the exercises array
      const updatedExercises = [...prev.esercizi];
      const currentExercise = updatedExercises[index];
      
      // Validazione: se esercizio è cardio, non può essere isometrico nella stessa scheda
      if (field === 'is_isometric_scheda' && value === 1) {
        const fullExercise = availableExercises.find(e => parseInt(e.id) === parseInt(currentExercise.esercizio_id));
        if (fullExercise?.is_cardio === 1) {
          setError('Un esercizio cardio non può essere isometrico nella stessa scheda');
          setTimeout(() => setError(null), 3000);
          return prev; // Non modificare
        }
      }
      
      // Determine the processed value based on the field type
      let processedValue = value;
      switch (field) {
        case 'esercizio_id':
        case 'serie':
        case 'ripetizioni':
          processedValue = parseInt(value, 10);
          break;
        case 'peso':
          processedValue = parseFloat(value);
          break;
        case 'tempo_recupero':
          processedValue = parseInt(value, 10) || 90;
          break;
        case 'set_type':
          // Validazione speciale per set_type
          processedValue = validateSetType(value);
          // Se è normale, linked_to_previous deve essere false
          if (processedValue === 'normal') {
            updatedExercises[index] = { 
              ...updatedExercises[index], 
              [field]: processedValue,
              linked_to_previous: false 
            };
            return {
              ...prev,
              esercizi: updatedExercises
            };
          }
          break;
        default:
          processedValue = value;
      }
      
      // Update the specific field for the exercise at the given index
      updatedExercises[index] = { 
        ...updatedExercises[index], 
        [field]: processedValue 
      };
      
      return {
        ...prev,
        esercizi: updatedExercises
      };
    });
  }, [availableExercises]);
  
  // Handle exercise reordering with drag-and-drop
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    
    if (active && over && active.id !== over.id) {
      setWorkoutData(prev => {
        // Find the indices of the dragged item and its destination
        const oldIndex = prev.esercizi.findIndex(item => `exercise-${item.tempId}` === active.id);
        const newIndex = prev.esercizi.findIndex(item => `exercise-${item.tempId}` === over.id);
        
        if (oldIndex !== -1 && newIndex !== -1) {
          // Update the ordine values
          const updatedExercises = arrayMove(prev.esercizi, oldIndex, newIndex).map((ex, index) => ({
            ...ex,
            ordine: index
          }));
          
          return {
            ...prev,
            esercizi: updatedExercises
          };
        }
        
        return prev;
      });
    }
  }, []);

  // Save the workout - Version migliorata
  const handleSave = async () => {
    if (saving) return;
    
    try {
      setSaving(true);
      setError(null);
      
      // Validate required fields
      if (!workoutData.nome.trim()) {
        throw new Error(labels.nomeRequired);
      }
      
      if (workoutData.esercizi.length === 0) {
        throw new Error(labels.exercisesRequired);
      }
      
      // Prepare the data for submission with robust validation
      const dataToSend = {
        nome: workoutData.nome.trim(),
        descrizione: workoutData.descrizione || '',
        active: workoutData.active ? 1 : 0,
        esercizi: workoutData.esercizi.map((ex, index) => {
          // Validazione esplicita di set_type - assicurati che sia sempre una stringa valida
          const validSetType = validateSetType(ex.set_type);
          
          // Trova l'esercizio completo per verificare se è cardio
          const fullExercise = availableExercises.find(e => parseInt(e.id) === parseInt(ex.esercizio_id));
          const isCardio = fullExercise?.is_cardio === 1;
          
          // Per esercizi cardio: converti minuti in secondi
          let ripetizioni = Math.max(1, parseInt(ex.ripetizioni, 10) || 10);
          if (isCardio) {
            // Se è cardio, ripetizioni contiene minuti nell'UI, converti in secondi per il DB
            ripetizioni = ripetizioni * 60;
          }
          
          // 🔥 DB-001: Gestione note nel formato JSON corretto
          let noteValue;
          if (ex.notes) {
            // Nuovo formato: oggetto notes
            noteValue = JSON.stringify(ex.notes);
          } else if (ex.note) {
            // Legacy: stringa semplice, converti in JSON (Admin: note vanno su trainer)
            noteValue = JSON.stringify({
              trainer: ex.note,
              user: null,
              system: null
            });
          } else {
            // Nessuna nota: JSON vuoto
            noteValue = JSON.stringify({ trainer: null, user: null, system: null });
          }
          
          // Base exercise data with explicit type conversion
          const exerciseData = {
            esercizio_id: parseInt(ex.esercizio_id, 10),
            serie: Math.max(1, parseInt(ex.serie, 10) || (isCardio ? 1 : 3)), // Cardio: sempre 1 serie
            ripetizioni: ripetizioni,
            peso: parseFloat(ex.peso) || 0,
            tempo_recupero: String(parseInt(ex.tempo_recupero, 10) || 90),
            note: noteValue,
            set_type: validSetType, // Usa la versione validata
            linked_to_previous: ex.linked_to_previous ? 1 : 0,
            ordine: index,
            is_isometric_scheda: ex.is_isometric_scheda || 0, // 🔥 DB-001: Usa is_isometric_scheda invece di is_isometric
            calorie: ex.calorie || null,
            bpm: ex.bpm || null
          };
          
          // IMPORTANTE: Preserva l'ID originale se esiste
          if (ex.id) {
            exerciseData.id = parseInt(ex.id, 10);
          }
          
          return exerciseData;
        })
      };
      
      let response;
      let schedaId = null;

      if (onCustomSave) {
        if (mode === 'edit' && initialWorkout?.id) {
          schedaId = initialWorkout.id;
        }
        await onCustomSave(dataToSend, { mode, entityId: schedaId });
        if (onSuccess) onSuccess({ id: schedaId });
        onClose();
        return;
      }
      
      // Determine which ID to use for the update
      if (mode === 'edit' && initialWorkout) {
        if (initialWorkout.id && !initialWorkout.scheda_id) {
          schedaId = initialWorkout.id;
        } else if (initialWorkout.scheda_id) {
          schedaId = initialWorkout.scheda_id;
        }
        
        try {
          if (schedaId) {
            // Update existing workout
            response = await api.put(`/schede.php?id=${schedaId}`, dataToSend);
          } else {
            throw new Error("ID scheda non valido per l'aggiornamento");
          }
        } catch (error) {
          console.error("Errore dettagliato:", {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status
          });
          throw error;
        }
      } else {
        // Create new workout
        response = await api.post('/schede.php', dataToSend);
        
        // Assign to user if it's a new workout
        if (response.data && response.data.id && user) {
          await api.post('/user_assignments.php', {
            user_id: user.id,
            scheda_id: response.data.id
          });
        }
      }
      
      if (response && response.data) {
        if (onSuccess) onSuccess(response.data);
        
        // Chiudi il modale
        onClose();
        
        // Aggiungi un breve ritardo prima di ricaricare la pagina
        setTimeout(() => {
          window.location.reload();
        }, 300);
      }
    } catch (err) {
      console.error('Error saving workout:', err);
      setError(err.response?.data?.error || err.message || 'Errore durante il salvataggio della scheda');
    } finally {
      setSaving(false);
    }
  };
  
  // Filter exercises based on search and active tab
  const filteredExercises = availableExercises.filter(exercise => {
    const matchesSearch = exercise.nome.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGroup = activeTab === 'tutti' || 
                         (exercise.gruppo_muscolare && 
                          exercise.gruppo_muscolare.toLowerCase() === activeTab);
    return matchesSearch && matchesGroup;
  });
  
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full text-center">
          <Loader2 className="animate-spin h-10 w-10 mx-auto mb-4 text-blue-600 dark:text-blue-400" />
          <p className="text-gray-600 dark:text-gray-300">{labels.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2 sm:p-4">
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full h-[95vh] max-h-[95vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Dumbbell className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            {mode === 'edit' ? labels.titleEdit : labels.titleNew}
            {user && !editorConfig?.hideUserInTitle && (
              <span className="text-lg font-normal text-gray-600 dark:text-gray-400">
                : {user.nome || user.username || ''}
              </span>
            )}
          </h2>
          
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={saving || workoutData.esercizi.length === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-green-400/50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4" />
                  <span>Salvataggio...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>{mode === 'edit' ? labels.saveEdit : labels.saveNew}</span>
                </>
              )}
            </button>
            
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
            >
              <XCircle className="h-4 w-4" />
              <span>Annulla</span>
            </button>
            
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              aria-label="Chiudi"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        {/* Error message */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}
        
        {/* Content area - scrollable */}
        <div className="flex flex-col md:flex-row overflow-hidden h-full">
          {/* Left column - Workout details & exercises list */}
          <div className="p-3 sm:p-4 flex-1 overflow-y-auto">
            <div className="max-w-3xl mx-auto space-y-6">
              {/* Workout details form */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label 
                    htmlFor="nome" 
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    {labels.nomeLabel}
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    value={workoutData.nome}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                    placeholder={labels.nomePlaceholder}
                    required
                  />
                </div>
                <div className="sm:col-span-2 flex items-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={workoutData.active}
                      onChange={handleActiveToggle}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                    <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {workoutData.active ? labels.activeOn : labels.activeOff}
                    </span>
                  </label>
                </div>
                <div className="sm:col-span-2">
                  <label 
                    htmlFor="descrizione"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Descrizione
                  </label>
                  <textarea
                    id="descrizione"
                    name="descrizione"
                    value={workoutData.descrizione}
                    onChange={handleInputChange}
                    rows="2"
                    className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                    placeholder={labels.descrizionePlaceholder}
                  />
                </div>
              </div>
              
              {/* Exercise List Title */}
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
                  <Dumbbell className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  Esercizi ({workoutData.esercizi.length})
                </h3>
              </div>

            {/* Sortable exercise list - Griglia stile Instagram */}
            {workoutData.esercizi.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={workoutData.esercizi.map(ex => `exercise-${ex.tempId}`)}
                    strategy={verticalListSortingStrategy}
                  >
                    {/* Griglia stile Instagram */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {workoutData.esercizi.map((exercise, index) => {
                        const isExpanded = expandedExercise === index;
                        const exerciseName = getExerciseName(exercise.esercizio_id);
                        const fullExercise = availableExercises.find(e => parseInt(e.id) === parseInt(exercise.esercizio_id));
                        const isCardio = fullExercise?.is_cardio === 1;
                        
                        // Determina l'URL dell'immagine
                        let imageUrl = null;
                        if (fullExercise) {
                          if (fullExercise.immagine_statica) {
                            imageUrl = fullExercise.immagine_statica;
                          } else if (fullExercise.immagine_nome) {
                            imageUrl = `/api/serve_image.php?filename=${encodeURIComponent(fullExercise.immagine_nome)}`;
                          }
                        }
                        
                        return (
                          <div key={exercise.tempId} className="relative">
                            {/* Visual indicator for linked exercises */}
                            {exercise.linked_to_previous && (
                              <div className="absolute -top-2 -left-2 z-10 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs font-bold">+</span>
                              </div>
                            )}
                            
                            <SortableExerciseItem
                              id={`exercise-${exercise.tempId}`}
                              className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:shadow-lg transition-shadow ${
                                exercise.linked_to_previous ? 'border-t-2 border-blue-500' : ''
                              }`}
                            >
                              {/* Immagine grande stile Instagram */}
                              <div className="relative aspect-square bg-gray-100 dark:bg-gray-700 overflow-hidden group">
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={exerciseName}
                                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      const placeholder = e.target.nextElementSibling;
                                      if (placeholder) placeholder.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div className={`${imageUrl ? 'hidden' : 'flex'} w-full h-full items-center justify-center`}>
                                  <Image className="h-16 w-16 text-gray-400" />
                                </div>
                                
                                {/* Overlay con pulsanti al hover */}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedExercise(isExpanded ? null : index);
                                    }}
                                    className="p-2 bg-white/90 dark:bg-gray-800/90 rounded-full text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-800 transition-colors"
                                    title="Modifica esercizio"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveExercise(index);
                                    }}
                                    className="p-2 bg-white/90 dark:bg-gray-800/90 rounded-full text-red-600 dark:text-red-400 hover:bg-white dark:hover:bg-gray-800 transition-colors"
                                    title="Rimuovi esercizio"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                                
                                {/* Drag handle - visibile al hover */}
                                <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity cursor-move">
                                  <GripVertical className="h-5 w-5 text-white drop-shadow-lg" />
                                </div>
                              </div>
                              
                              {/* Nome e info sotto l'immagine */}
                              <div className="p-3">
                                <h4 
                                  className="font-medium text-gray-900 dark:text-white text-sm mb-2 line-clamp-2 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                                  onClick={() => setExpandedExercise(isExpanded ? null : index)}
                                  title={exerciseName}
                                >
                                  {exerciseName}
                                </h4>
                                
                                {/* Informazioni principali compatte */}
                                <div className="space-y-1">
                                  <div className="text-xs text-gray-600 dark:text-gray-400">
                                    {exercise.serie} x {exercise.ripetizioni}
                                    {!isCardio && exercise.peso > 0 && ` @ ${exercise.peso}kg`}
                                  </div>
                                  
                                  <div className="flex flex-wrap items-center gap-1">
                                    {exercise.set_type !== 'normal' && (
                                      <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded">
                                        {SET_TYPES[exercise.set_type]}
                                      </span>
                                    )}
                                    {isCardio && (
                                      <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded">
                                        Cardio
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </SortableExerciseItem>
                            
                            {/* Modal per modificare i dettagli quando espanso */}
                            {isExpanded && (
                              <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" onClick={() => setExpandedExercise(null)}>
                                <div 
                                  className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                      <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                        <Dumbbell className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                                        {exerciseName}
                                      </h3>
                                      <button
                                        onClick={() => setExpandedExercise(null)}
                                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                      >
                                        <X className="h-6 w-6" />
                                      </button>
                                    </div>
                                    
                                    {/* Form modifica esercizio */}
                                    <div className="space-y-4">
                                      {/* Tipo di set */}
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                          Tipo di Set
                                        </label>
                                        <select
                                          value={exercise.set_type || 'normal'}
                                          onChange={(e) => handleExerciseChange(index, 'set_type', e.target.value)}
                                          className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                                        >
                                          {Object.entries(SET_TYPES).map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                          ))}
                                        </select>
                                      </div>
                                      
                                      {/* Dettagli esercizio */}
                                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                        <div>
                                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                            Serie
                                          </label>
                                          <input
                                            type="number"
                                            value={exercise.serie}
                                            onChange={(e) => handleExerciseChange(index, 'serie', e.target.value)}
                                            min="1"
                                            className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                                          />
                                        </div>
                                        
                                        <div>
                                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                            {isCardio ? 'Minuti' : (exercise.is_isometric_scheda === 1 ? 'Secondi' : 'Ripetizioni')}
                                          </label>
                                          <input
                                            type="number"
                                            value={exercise.ripetizioni}
                                            onChange={(e) => handleExerciseChange(index, 'ripetizioni', e.target.value)}
                                            min="1"
                                            className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                                          />
                                        </div>
                                        
                                        {!isCardio && (
                                          <div>
                                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                              Peso (kg)
                                            </label>
                                            <input
                                              type="number"
                                              value={exercise.peso}
                                              onChange={(e) => handleExerciseChange(index, 'peso', e.target.value)}
                                              min="0"
                                              step="0.5"
                                              className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                                            />
                                          </div>
                                        )}
                                        
                                        <div>
                                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                            Recupero (sec)
                                          </label>
                                          <input
                                            type="number"
                                            value={exercise.tempo_recupero}
                                            onChange={(e) => handleExerciseChange(index, 'tempo_recupero', e.target.value)}
                                            min="0"
                                            step="5"
                                            className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                                          />
                                        </div>
                                      </div>
                                      
                                      {/* Checkbox per linked_to_previous */}
                                      {index > 0 && exercise.set_type !== 'normal' && (
                                        <div>
                                          <label className="flex items-center">
                                            <input
                                              type="checkbox"
                                              checked={exercise.linked_to_previous || false}
                                              onChange={(e) => handleExerciseChange(index, 'linked_to_previous', e.target.checked)}
                                              className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                              Collega all'esercizio precedente ({getExerciseName(workoutData.esercizi[index-1].esercizio_id)})
                                            </span>
                                          </label>
                                        </div>
                                      )}
                                      
                                      {/* Modalità Isometrica */}
                                      {!isCardio && (
                                        <div>
                                          <label className="flex items-center">
                                            <input
                                              type="checkbox"
                                              checked={exercise.is_isometric_scheda === 1}
                                              onChange={(e) => handleExerciseChange(index, 'is_isometric_scheda', e.target.checked ? 1 : 0)}
                                              className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                              Modalità Isometrica (usa secondi invece di ripetizioni)
                                            </span>
                                          </label>
                                        </div>
                                      )}
                                      
                                      {/* Campi Cardio */}
                                      {isCardio && (
                                        <div className="grid grid-cols-2 gap-4">
                                          <div>
                                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                              Calorie (opzionale)
                                            </label>
                                            <input
                                              type="number"
                                              min="0"
                                              value={exercise.calorie || ''}
                                              onChange={(e) => handleExerciseChange(index, 'calorie', e.target.value ? parseInt(e.target.value) : null)}
                                              className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                              BPM (opzionale)
                                            </label>
                                            <input
                                              type="number"
                                              min="0"
                                              value={exercise.bpm || ''}
                                              onChange={(e) => handleExerciseChange(index, 'bpm', e.target.value ? parseInt(e.target.value) : null)}
                                              className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                                            />
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Note */}
                                      <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                          Note
                                        </label>
                                        <textarea
                                          value={exercise.note || ''}
                                          onChange={(e) => handleExerciseChange(index, 'note', e.target.value)}
                                          className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                                          rows="3"
                                          placeholder="Note sull'esecuzione dell'esercizio..."
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
            ) : (
                            <div className="text-center p-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                            <Dumbbell className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-gray-500 dark:text-gray-400 mb-4">
                            Nessun esercizio aggiunto.
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                            Usa il pannello a destra per selezionare gli esercizi da aggiungere alla scheda.
                            </p>
                            </div>
                            )}
                            </div>
                            </div>

                            {/* Right column - Exercise selector */}
                            <div className="w-full md:w-80 lg:w-96 xl:w-[30%] border-t md:border-t-0 md:border-l border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-950">
                            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="font-medium text-gray-900 dark:text-white mb-3">
                            Seleziona Esercizi
                            </h3>

                            {/* Search */}
                            <div className="relative mb-3">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Cerca esercizi..."
                            className="w-full pl-9 pr-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                            </div>
                            {/* Tab navigation */}
              <div className="flex overflow-x-auto pb-1 -mx-1">
                {Object.entries(MUSCLE_GROUPS).map(([value, label]) => (
                  <button
                    key={value}
                    onClick={() => setActiveTab(value)}
                    className={`px-3 py-1.5 mx-0.5 text-xs font-medium rounded-md whitespace-nowrap ${
                      activeTab === value
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Exercise list */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {filteredExercises.length === 0 ? (
                <div className="text-center p-4 text-gray-500 dark:text-gray-400">
                  {searchTerm 
                    ? 'Nessun esercizio trovato per la ricerca.' 
                    : 'Nessun esercizio disponibile per questo gruppo muscolare.'}
                </div>
              ) : (
                filteredExercises.map(exercise => (
                  <div 
                    key={exercise.id}
                    className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex gap-2">
                        {/* Miniatura dell'immagine */}
                        <div className="w-10 h-10 flex-shrink-0 rounded overflow-hidden bg-gray-100 dark:bg-gray-600">
                          {exercise.immagine_statica ? (
                            <img
                              src={exercise.immagine_statica}
                              alt=""
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '';
                                // Verifica che parentNode esista prima di accedervi
                                if (e.target.parentNode) {
                                  e.target.parentNode.innerHTML = `<div class="flex items-center justify-center w-full h-full">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400">
                                      <rect x="2" y="2" width="20" height="20" rx="2" ry="2"></rect>
                                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                      <polyline points="21 15 16 10 5 21"></polyline>
                                    </svg>
                                  </div>`;
                                }
                              }}
                            />
                          ) : (
                            <div className="flex items-center justify-center w-full h-full">
                              <ImageIcon className="h-4 w-4 text-gray-400" />
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                            {exercise.nome}
                          </h4>
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
                              {exercise.gruppo_muscolare}
                            </span>
                            {(() => {
                              const fullExercise = availableExercises.find(e => parseInt(e.id) === parseInt(exercise.id));
                              const isCardio = fullExercise?.is_cardio === 1;
                              return isCardio ? (
                                <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded-full">
                                  Cardio
                              </span>
                              ) : null;
                            })()}
                            {exercise.attrezzatura && (
                              <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-300 rounded-full">
                                {exercise.attrezzatura}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-1">
                        <button
                          type="button"
                          title="Informazioni"
                          className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                          onClick={() => setSelectedExerciseInfo(exercise)}
                        >
                          <Info className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          title="Aggiungi esercizio"
                          onClick={() => handleAddExercise(exercise)}
                          className="p-1.5 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          <Plus className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* ExerciseInfoDialog */}
      {selectedExerciseInfo && (
        <ExerciseInfoDialog 
          exercise={selectedExerciseInfo} 
          onClose={() => setSelectedExerciseInfo(null)} 
        />
      )}
    </div>
  );
};

export default ImprovedWorkoutCreator;